/**
 * One day's events, for the desk Year's day pane and desk Browse's
 * `day=` (a3-20). Decided here, with no React and only `deskBrowse.ts` and `zone.ts` (which
 * load without a bundler), so the Worker's suite tests it and the pane's count and
 * Browse's list are made by the same rule: "96 on this day · see in Browse"
 * has to open a list of 96.
 */
import { isRunning, startOf, type DeskRow } from './deskBrowse.ts'
import { dayIn, zoneOfCity } from './zone.ts'

export interface Timed { starts_at: string }

const shift = (day: string, n: number) =>
  new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10)

/** The first of a row's own showings that falls on `day` in `tz` — the row's
 *  zone (C2.1) — or null. */
export function firstOn(showings: readonly Timed[], day: string, tz: string): string | null {
  // A local day reaches at most a day either side of its UTC date, so a
  // string compare narrows the list before `dayIn` — an `Intl` call, and
  // Browse asks this of every row — is asked.
  const near = [shift(day, -1), day, shift(day, 1)]
  let first: string | null = null
  for (const s of showings) {
    if (!near.includes(s.starts_at.slice(0, 10)) || dayIn(s.starts_at, tz) !== day) continue
    if (first === null || s.starts_at < first) first = s.starts_at
  }
  return first
}

/**
 * Is a row on this day. A running exhibition is on every day of its run; a
 * row with showings is on the days they fall on, not only the next one (a
 * film playing Tuesday and Thursday is on both); a row with none is on the
 * day it starts.
 */
export function isOnDay(row: DeskRow, kind: string, showings: readonly Timed[], day: string): boolean {
  if (isRunning(row, kind)) return (row.run_start as string) <= day && day <= (row.run_end as string)
  if (showings.length) return firstOn(showings, day, zoneOfCity(row.city)) !== null
  return startOf(row)?.day === day
}

/**
 * The pane's slots, in the order Doug named them: Concert, Theatre and opera,
 * Film, Club, Exhibition, then the rest. Opera shares theatre's slot — one
 * evening at a stage — where Browse's chips keep them apart.
 */
export const DAY_SLOTS: readonly { slot: string; label: string }[] = [
  { slot: 'concert', label: 'Concert' },
  { slot: 'theatre', label: 'Theatre and opera' },
  { slot: 'film', label: 'Film' },
  { slot: 'club', label: 'Club' },
  { slot: 'exhibition', label: 'Exhibition' },
  { slot: 'gig', label: 'Gig' },
  { slot: 'talk', label: 'Talk' },
  { slot: 'quiz', label: 'Quiz' },
  { slot: 'market', label: 'Market' },
]

/** A `Map`, not an object literal: `SLOT_OF['constructor']` would answer
 *  (CLAUDE.md, the tab-alias bug). */
const SLOT_OF = new Map<string, string>([
  ...DAY_SLOTS.map(s => [s.slot, s.slot] as [string, string]),
  ['opera', 'theatre'],
])

/**
 * The best-scored row of each kind, one per slot, in `DAY_SLOTS` order. Ties
 * keep the order given. A kind with no slot is left out rather than guessed.
 */
export function bestPerKind<T>(
  rows: readonly T[], kindOf: (t: T) => string, score: (t: T) => number,
): { slot: string; label: string; item: T }[] {
  const best = new Map<string, T>()
  for (const r of rows) {
    const slot = SLOT_OF.get(kindOf(r))
    if (!slot) continue
    const held = best.get(slot)
    if (held === undefined || score(r) > score(held)) best.set(slot, r)
  }
  return DAY_SLOTS.filter(s => best.has(s.slot)).map(s => ({ ...s, item: best.get(s.slot) as T }))
}
